import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Location } from "history";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { TableChangeParams } from "../../../../components/Table/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import { EnumPaths } from "../../../../utils/navigation";
import history from "../../../../history";
import Table from "../../../../components/Table";
import Dropdown, {
  DropdownValues,
} from "../../../../components/Tailwind/Dropdown";
import images from "../../../../images";
import { getTranslation } from "../../../../utils/translation";
import ConfirmModal from "../../../../utils/modal/confirm";
import IntlMessages from "../../../../utils/messages";
import {
  displayDayDDMMYYYY_HHMM,
  DEFAULT_LIMIT,
} from "../../../../utils/config";
import { IncidentStatus } from "../../../../lib/ground-aws-graphql-core/models/Api/Incidents";

interface Props {
  intl: IntlShape;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListIncidentStatus = (props: Props): JSX.Element => {
  const { intl, location } = props;
  const [loading, setLoading] = useState(false);

  const [showDeleteIncidentStatusModal, setShowDeleteIncidentStatusModal] =
    useState(false);

  const [incidentStatusId, setIncidentStatusId] = useState<number>();
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listIncidentStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.incidents.listIncidentStatus
  );

  const incidentsStatus = GroundGraphqlContextStore.useStoreState(
    actions => actions.incidents.incidentsStatus.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    actions => actions.incidents.incidentsStatus.total
  );

  const setIncidentStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.incidents.setIncidentStatus
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const deleteIncidentStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.incidents.deleteIncidentStatus
  );

  useEffect(() => {
    if (location?.state) {
      fetchData(location?.state?.pageIndex, true, location?.state?.limit);
    } else fetchData();
  }, []);

  const fetchData = (pageIndex = 0, loader = true, limit = DEFAULT_LIMIT) => {
    if (loader) setLoading(true);

    listIncidentStatus({
      offset: pageIndex * limit,
      limit,
    }).finally(() => setLoading(false));
  };

  const addNewIncidentStatus = () => {
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.INCIDENT_STATUS}/${EnumPaths.CREATION_MODE}`
    );
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit } = changeParams;
    setInitialParams(changeParams);
    fetchData(pageIndex, false, limit);
  };

  const handleEditIncidentStatus = (id: number) => {
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.INCIDENT_STATUS}/${id}/${EnumPaths.EDIT_MODE}`;
    setIncidentStatus(null);
    history.push(editLink);
  };

  const handleDelete = () => {
    setLoading(true);
    setShowDeleteIncidentStatusModal(false);
    deleteIncidentStatus({ id: incidentStatusId! })
      .then(() => {
        toast(
          intl.formatMessage({
            id: "page.incident.status.delete.success",
          }),
          { type: "success" }
        );
      })
      .catch(() => {
        toast(
          intl.formatMessage({
            id: "page.incident.status.delete.error",
          }),
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const tableHead = [
    "page.incident.status.label",
    "page.incident.status.code",
    "page.incident.status.created_at",
    "page.incident.status.updated_at",
    "general.actions",
  ];

  const tableBody = incidentsStatus.map((incidentStatus: IncidentStatus) => {
    const { id, code, label, created_at, updated_at } = incidentStatus;
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.INCIDENT_STATUS}/${id}/${EnumPaths.EDIT_MODE}`;

    const dropdownValues: DropdownValues[] = [
      {
        id: "edit_incident_status_key",
        label: "page.list.incident.status.edit",
        icon: images.edit,
        link: editLink,
      },
      {
        id: "delete",
        label: "general.delete",
        icon: images.deleteIcon,
        onClick: () => {
          setShowDeleteIncidentStatusModal(!showDeleteIncidentStatusModal);
          setIncidentStatusId(id);
        },
      },
    ];

    const incidentElements = [
      {
        element: getTranslation(label),
        onCellClick: () => handleEditIncidentStatus(id),
      },
      {
        element: code,
        onCellClick: () => handleEditIncidentStatus(id),
      },
      {
        element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
        onCellClick: () => handleEditIncidentStatus(id),
      },
      {
        element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
        onCellClick: () => handleEditIncidentStatus(id),
      },
      {
        element: (
          <Dropdown
            values={dropdownValues}
            dataCy={`incident-status-dropdown-${id}`}
          />
        ),
      },
    ];

    return {
      id: incidentStatus.id.toString(),
      rowElements: incidentElements,
    };
  });

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: `page.list.incident.status.document.title`,
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.incident.status.title"
        nbOfResults={total}
        buttonRightTitle="page.list.incident.status.create.incident.status"
        buttonRightAction={addNewIncidentStatus}
        buttonRightId="btn-create-incident-status"
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.incident.status.empty"
        onChange={handleOnChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />

      <ConfirmModal
        isOpen={showDeleteIncidentStatusModal}
        onRequestClose={() =>
          setShowDeleteIncidentStatusModal(!showDeleteIncidentStatusModal)
        }
        toggle={() =>
          setShowDeleteIncidentStatusModal(!showDeleteIncidentStatusModal)
        }
        handleConfirm={handleDelete}
        content={<IntlMessages id="page.list.incident.status.delete" />}
      />
    </div>
  );
};

export default injectIntl(ListIncidentStatus);
